import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const QUICK_REPLIES = [
  'Print this',
  'For parents',
  'For socials',
  'Edit object out',
  'Crop tighter',
];

interface NoteSheetProps {
  open: boolean;
  initialNote?: string;
  onClose: () => void;
  onSave: (note: string) => void;
}

export function NoteSheet({ open, initialNote, onClose, onSave }: NoteSheetProps) {
  const [text, setText] = useState(initialNote ?? '');

  useEffect(() => {
    if (open) setText(initialNote ?? '');
  }, [open, initialNote]);

  const toggleChip = (chip: string) => {
    setText((t) => {
      const has = t.toLowerCase().includes(chip.toLowerCase());
      if (has) return t;
      const sep = t.trim().length > 0 ? '\n' : '';
      return t + sep + chip;
    });
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/55 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            aria-hidden
          />
          <motion.div
            className="fixed left-0 right-0 bottom-0 z-50 bg-[var(--surface-primary)] border border-[var(--border-default)] border-b-0 rounded-t-3xl px-5 pt-3 pb-7 flex flex-col gap-3.5 mx-auto md:max-w-[440px]"
            style={{ boxShadow: '0 -20px 40px rgba(0,0,0,0.6)' }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 400, damping: 40 }}
            role="dialog"
            aria-modal="true"
            aria-label="Add a note"
          >
            <div className="w-9 h-1 rounded-full bg-[var(--border-strong)] mx-auto" />
            <div className="text-[17px] font-semibold">Add a note</div>
            <div className="flex flex-wrap gap-1.5">
              {QUICK_REPLIES.map((chip) => {
                const active = text.toLowerCase().includes(chip.toLowerCase());
                return (
                  <button
                    key={chip}
                    onClick={() => toggleChip(chip)}
                    className={[
                      'px-3 py-1.5 rounded-full text-xs border transition-colors',
                      active
                        ? 'bg-[var(--accent-primary)] text-[#0a0a0a] border-[var(--accent-primary)] font-semibold'
                        : 'bg-[var(--surface-secondary)] text-[var(--text-secondary)] border-[var(--border-subtle)]',
                    ].join(' ')}
                  >
                    {chip}
                  </button>
                );
              })}
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Anything else? Notes are private — only your photographer sees them."
              className="bg-[var(--surface-secondary)] border border-[var(--border-default)] rounded-2xl p-3.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] resize-none focus:outline-none focus:border-[var(--border-strong)] min-h-24"
              autoFocus
            />
            <button
              onClick={() => {
                onSave(text);
                onClose();
              }}
              className="h-12 rounded-2xl bg-[var(--accent-primary)] text-[#0a0a0a] font-semibold text-sm"
            >
              Save note
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
